import { Component, Input, Output, ViewChild, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DialogService } from '@caslink/common-client/ng-app/dialog-service/dialog.service';
import { FolderNameDialogComponent } from '../folder-name-dialog/folder-name-dialog.component';
import { Subject, Subscription } from 'rxjs';
import { IJobDocumentTreeNodeViewModel, IAddJobDocumentFolderRequest, IJobDocumentTreeViewModel } from '../shared/models/nola-job-document-models';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { NolaJobDocumentService } from '../nola-job-document/nola-job-document.service';
import { SelectionModel } from "@angular/cdk/collections";
import { MessageType } from '@caslink/common-client';
import * as moment from 'moment';

@Component({
    selector: 'cl-nola-job-document',
    templateUrl: './nola-job-document.component.html',
    styleUrls: ['./nola-job-document.component.scss']
})
export class NolaJobDocumentComponent implements OnDestroy, OnInit {
    static $name: string = 'clNolaJobDocument';

    @Input() public hardwareItemId: number;
    @Input() public caalMode: boolean;
    @Input() public multiSelect: boolean;

    @Output() selectionChanged = new EventEmitter();

    public isReady: boolean = false;

    treeControl = new NestedTreeControl<IJobDocumentTreeNodeViewModel>(node => node.childDocuments);
    dataSource = new MatTreeNestedDataSource<IJobDocumentTreeNodeViewModel>();

    checklistSelection: SelectionModel<IJobDocumentTreeNodeViewModel>;

    public canDownloadDocument: boolean;
    public canDeleteDocument: boolean;
    private selectedNodeData: IJobDocumentTreeNodeViewModel;

    private file: File;

    public search: string = '';
    public searchSubject = new Subject<string>();
    public showInfo: boolean;
    public infoDisplayName: string;
    public infoDocumentName: string;
    public infoFileSize: number;
    public infoCreatedBy: string;
    public infoCreatedOn: string;
    public infoIsDirectory: boolean;
    public dataLoadedError = false;

    private searchSubscription: Subscription;

    @ViewChild(MatMenuTrigger, {static: false, read: null})
    contextMenu: MatMenuTrigger;

    contextMenuPosition = { x: '0px', y: '0px' };

    constructor(
        private dialogService: MatDialog,
        private confirmationDialogService: DialogService,
        private nolaJobDocumentService: NolaJobDocumentService
    ) {
    }

    hasChild = (_: number, node: IJobDocumentTreeNodeViewModel) => !!node.childDocuments && node.childDocuments.length > 0;

    ngOnInit() {
        this.initSearchEvent();
        this.getJobDocumentTree(this.hardwareItemId);
    }

    private getJobDocumentTree(
        hardwareItemId: number
    ) {
        if (this.selectedNodeData != null) {
            this.selectedNodeData = null; //since we reload tree no need to keep that selection
            this.canDownloadDocument = false; //since we reload tree nothing will be checked initially
            this.canDeleteDocument = false; //since we reload tree nothing will be checked initially
            this.selectionChanged.emit(this.selectedNodeData);
        }

        this.nolaJobDocumentService.getJobDocumentTreeImp(hardwareItemId, this.search).then((result: IJobDocumentTreeViewModel) => {
            var nodes: IJobDocumentTreeNodeViewModel[] = result.nodes;

            //api retruns an array with one null element if there is no jobNumber
            if (result.jobNumberIsMissing) {
                this.caalMode = true;
            }

            this.checklistSelection = new SelectionModel<IJobDocumentTreeNodeViewModel>(
                true /* multiple */
            );

            this.checklistSelection.changed.subscribe(change => {
                this.canDownloadDocument = this.GetSelectedNodes(false, false).length > 0;
                this.canDeleteDocument = this.GetSelectedNodes(true, true).length > 0;
            });

            this.dataSource.data = nodes;

            this.treeControl.dataNodes = nodes;

            this.treeControl.expandAll();

        }).catch(() => {
            this.dataLoadedError = true;
        }).finally(() => {
            this.isReady = true;
        });
    }

    ngOnDestroy() {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    public nodeSelected(nodeData: IJobDocumentTreeNodeViewModel) {
        if (this.selectedNodeData != null) {
            var oldelem = document.getElementById( (this.multiSelect ? "" : "x") + this.selectedNodeData.documentId.toString());
            if (oldelem != null) {
                oldelem.style.background = "";
            }
        }


        // only can add that when particular folder selected
        this.selectedNodeData = nodeData;
        var newelem = document.getElementById((this.multiSelect ? "" : "x") +  this.selectedNodeData.documentId.toString());
        newelem.style.background = "darkgrey";


        this.canDownloadDocument = this.GetSelectedNodes(false, false).length > 0;
        this.canDeleteDocument = this.GetSelectedNodes(true, true).length > 0;

        this.showInfo = true;
        this.infoCreatedBy = this.selectedNodeData.createdBy;
        this.infoCreatedOn = moment(this.selectedNodeData.createdOn).format('lll');
        this.infoDisplayName = this.selectedNodeData.displayName;
        this.infoDocumentName = this.selectedNodeData.documentName;
        this.infoFileSize = this.selectedNodeData.fileSize;
        this.infoIsDirectory = this.selectedNodeData.isDirectory;

        this.selectionChanged.emit(this.selectedNodeData);
        
    }

    //////////////////////////////////////////////////////////////////////////////////
    //MENUE
    //////////////////////////////////////////////////////////////////////////////////

    public onContextMenu(event: any, node: IJobDocumentTreeNodeViewModel) {
        if (this.caalMode) return;

        event.preventDefault();

        var elem = document.getElementById("DocTree");

        let rect = elem.getBoundingClientRect();
        let x = event.clientX - rect.left; 
        let y = event.clientY - rect.top;

        this.contextMenuPosition.x = x + 'px';
        this.contextMenuPosition.y = y + 'px';

        this.contextMenu.menuData = { 'node': node };
        this.contextMenu.menu.focusFirstItem('mouse');
        this.contextMenu.openMenu();
    }


    public onContextMenuActionDownloadFile(node: IJobDocumentTreeNodeViewModel) {
        try {
            this.isReady = false;
            this.nolaJobDocumentService.downloadDocumentImp({ hardwareItemId: this.hardwareItemId, documentId: node.documentId }).subscribe((responseBody) => {
                this.isReady = true;
            });
        }
        catch (err) {
            this.isReady = true;
        }
    }

    public onContextMenuActionDeleteFile(node: IJobDocumentTreeNodeViewModel) {
        const options = {
            data: {
                confirmBtnText: 'Yes',
                message: 'Would you really like to remove file ' + node.displayName + '?',
                messageType: MessageType.Warning,
                showCancelBtn: true,
                title: 'Remove?'
            }
        };

        const dialogRef = this.confirmationDialogService.showConfirmDialog(options);

        dialogRef
            .afterClosed()
            .toPromise()
            .then(isConfirmed => {
                if (!isConfirmed) return;
                this.isReady = false;
                this.nolaJobDocumentService.deleteJobDocument(this.hardwareItemId, node.documentId).then((result) => {
                    this.getJobDocumentTree(this.hardwareItemId);
                });

            });
    }


    public onContextMenuActionAddFolder(node: IJobDocumentTreeNodeViewModel) {
        this.addNewFolderImp(node.documentId);
    }


    public onContextMenuActionDeleteFolder(node: IJobDocumentTreeNodeViewModel) {
        const options = {
            data: {
                confirmBtnText: 'Yes',
                message: 'Would you really like to remove folder ' + node.displayName + ((node.childDocuments.length > 0) ?  ' with all sub-folders and documents in them?' : ''),
                messageType: MessageType.Warning,
                showCancelBtn: true,
                title: 'Remove?'
            }
        };

        const dialogRef = this.confirmationDialogService.showConfirmDialog(options);

        dialogRef
            .afterClosed()
            .toPromise()
            .then(isConfirmed => {
                if (!isConfirmed) return;
                this.isReady = false;
                this.nolaJobDocumentService.deleteJobDocument(this.hardwareItemId, node.documentId).then((result) => {
                    this.getJobDocumentTree(this.hardwareItemId);
                });
            });
    }

    initSearchEvent() {
        this.searchSubscription = this.searchSubject
        .pipe(
            debounceTime(1000),
            distinctUntilChanged()
        )
        .subscribe(()=> {
            this.isReady = false;
            this.getJobDocumentTree(this.hardwareItemId);
        });
    }

    onSearchChange(value: string) {
        this.searchSubject.next(value);
    }

    ////////////////////////////////////////////////////////////////////////////////
    //OTHER BUSINESS COMMANDS
    ////////////////////////////////////////////////////////////////////////////////
    private GetSelectedNodes(includefolders: boolean, icludemissing : boolean): IJobDocumentTreeNodeViewModel[] {
        var nodes: IJobDocumentTreeNodeViewModel[] = [];
        for (let item of this.checklistSelection.selected) {
            if (!nodes.find(x => x.documentId == item.documentId) && (!item.isDirectory || includefolders) && (!item.isMissing || icludemissing)) {
                nodes.push(item);
            }
        }

        if (this.selectedNodeData != null) {
            if (!nodes.find(x => x.documentId == this.selectedNodeData.documentId) && (!this.selectedNodeData.isDirectory || includefolders) && (!this.selectedNodeData.isMissing || icludemissing)) {
                nodes.push(this.selectedNodeData);
            }
        }

        return nodes;
    }

    public downloadDocument() {

        var nodes: IJobDocumentTreeNodeViewModel[] = this.GetSelectedNodes(false, false);

        let count: number = 0;
        const length: number = nodes.length;

        if (length > 0) {
            this.isReady = false;
        }

        try {
            for (let i = 0; i < length; i++) {

                this.nolaJobDocumentService.downloadDocumentImp({ hardwareItemId: this.hardwareItemId, documentId: nodes[i].documentId }).subscribe((responseBody) => {
                    if (++count == length) {
                        this.getJobDocumentTree(this.hardwareItemId);
                    }

                });

            }

        }
        catch (err) {
            this.isReady = true;
        }


    }

    public deleteDocument() {

        var messages: string[] = [];
        const nodes = this.GetSelectedNodes(true, true);
        for (let i = 0; i < nodes.length; i++) {
            messages.push( nodes[i].displayName);
        }

        const options = {
            data: {
                confirmBtnText: 'Yes',
                message: 'Would you really like to remove selected files and folders with all sub-folders and documents in them?',
                messages: messages,
                formatter: function (item) { return item;},
                messageType: MessageType.Warning,
                showCancelBtn: true,
                title: 'Remove?'
            }
        };

        const dialogRef = this.confirmationDialogService.showConfirmDialog(options);

        dialogRef
            .afterClosed()
            .toPromise()
            .then(isConfirmed => {
                if (!isConfirmed) return;

                this.deleteDocumentImp();
            });
    }


    private deleteDocumentImp() {
        var nodes: IJobDocumentTreeNodeViewModel[] = this.GetSelectedNodes(true, true);

        let count: number = 0;
        const length: number = nodes.length;

        let documentIds: number[] = [];
        for (let i = 0; i < length; i++) {
            documentIds.push(nodes[i].documentId);
        }
        this.isReady = false;
        this.nolaJobDocumentService.deleteJobDocuments(this.hardwareItemId, documentIds).subscribe((responseBody) => {
            this.getJobDocumentTree(this.hardwareItemId);
        });
    }

    public addNewFolder() {
        this.addNewFolderImp(null);
    }

    private addNewFolderImp(parentDocumentId?: number) {

        const dialogRef = this.dialogService.open(FolderNameDialogComponent, {
            height: '250px',
            data: {
                folder: ''
            }
        });

        dialogRef.afterClosed().toPromise().then((result: any) => {
            if (result != null) {
                const request: IAddJobDocumentFolderRequest = {
                    folderName: result.folder,
                    parentDocumentId: parentDocumentId,
                    hardwareItemId: this.hardwareItemId
                }

                this.isReady = false;

                this.nolaJobDocumentService.addJobDocumentFolderImp(request).then((node: IJobDocumentTreeNodeViewModel) => {

                    this.getJobDocumentTree(this.hardwareItemId);
                });

            }
        });

    }





    ////////////////////////////////////////////////////////////////////////////////
    //ADD FILE UPLOAD
    //////////////////////////////////////////////////////////////////////////////////
    public onAddFilechange(event: any) {
        this.file = event.target.files[0];
        if (this.file != null) {
            this.doAddDocument();
        }
        event.target.value = '';
    }

    private doAddDocument() {
        this.isReady = false;
        this.nolaJobDocumentService.uploadAddFile(this.file, this.selectedNodeData, this.hardwareItemId).subscribe((responseBody) => {
            this.getJobDocumentTree(this.hardwareItemId);
        });
    }



    ////////////////////////////////////////////////////////////////////////////////
    //NEW FILE UPLOAD
    //////////////////////////////////////////////////////////////////////////////////
    public onNewFilechange(event: any) {
        this.file = event.target.files[0];
        if (this.file != null) {
            this.doNewDocument();
        }
        event.target.value = '';
    }

    private doNewDocument() {
        this.isReady = false;
        this.nolaJobDocumentService.uploadNewFile(this.file, this.hardwareItemId).subscribe((responseBody) => {
            this.getJobDocumentTree(this.hardwareItemId);
        });
    }


    //////////////////////////////////////////////////////////////////////////////////
    //CHECK BOXES
    //////////////////////////////////////////////////////////////////////////////////

    /** Toggle the to-do item selection. Select/deselect all the descendants node */
    public todoItemSelectionToggle(node: IJobDocumentTreeNodeViewModel): void {
        this.checklistSelection.toggle(node);
        const descendants = this.treeControl.getDescendants(node);
        this.checklistSelection.isSelected(node)
            ? this.checklistSelection.select(...descendants)
            : this.checklistSelection.deselect(...descendants);
    }

}
